import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PageShell } from '../layout/page-shell.jsx';
import { StatCard } from '../ui/stat-card.jsx';
import { useToast } from '../ui/toast.jsx';
import { formatUtcInTimezone } from '../../lib/time.js';
import { api, ApiError } from '../../api/index.js';

/**
 * COP ADMS · Datasets — faithful port of the reference page
 * `manuals/COP-microservice-integration/Datasets/layers-tab.jsx`.
 *
 * Architecture (per manuals/COP-microservice-integration/COMMON.md §1):
 *   Browser (admin session) → coord /api/cop-proxy/* → ad-cop-service.
 *
 * Adjustments vs the reference (clearly listed so future re-syncs are
 * trivial):
 *   1. `useTimezone()` context → `currentUser.timezone` prop + coord's
 *      `formatUtcInTimezone()` helper from `lib/time.js`.
 *   2. `onToast?.(msg)` callback → coord's `useToast()`. We map the
 *      reference's single-string toast to `success`/`error` based on
 *      success vs caught error context.
 *   3. `api.getCopXxx() / api.getAwdsXxx()` → `api.cop.xxx()` proxied
 *      through `/api/cop-proxy/*`.
 *   4. `createInstanceCacheSvg` (client-side milsymbol render — only
 *      used by the green paintbrush "Render SVG cache" button) is NOT
 *      ported in v1 — needs the full `map-object-instance-tab.jsx`
 *      port (~2100 lines + milsymbol vendor). INTEGRATION.md §6
 *      grabli #1 prescribes hiding that button until the Layers
 *      editor page lands. The blue MilSymbol button (server-side
 *      cache render) IS kept — server endpoint, no client deps.
 *   5. Class-name tokens that differ between reference's Tailwind
 *      config and ours:
 *           bg-accentSoft   → bg-accent-soft
 *           bg-emeraldSoft  → bg-emerald-50
 *           bg-amberSoft    → bg-amber-50
 *           bg-roseSoft     → bg-rose-soft
 *           shadow-panel    → shadow-card
 *      Everything else (standard Tailwind palettes, FontAwesome Pro 7
 *      sharp variants, btn-primary/-secondary/.field/.card utilities)
 *      is already in coord's index.css + tailwind.config.js.
 *   6. The whole page is wrapped in a `<PageShell>` so it matches the
 *      header / scroll container shape every other coord page uses.
 *   7. Reachability banner stays above the content — when ad-cop-
 *      service is missing / unreachable, the page renders the friendly
 *      "not installed" notice instead of trying to fetch ghosts.
 */

// ───────────────────────────────────────────────────────────────────
//  Portal-rendered Export menu (mirrors reference component verbatim).
// ───────────────────────────────────────────────────────────────────
function ExportMenu({ anchorRect, onClose, onSelect }) {
  const ref = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    function onEsc(e) { if (e.key === 'Escape') onClose(); }
    function onScroll() { onClose(); }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  if (!anchorRect) return null;

  const W = 192;
  const margin = 8;
  let left = anchorRect.right - W;
  let top = anchorRect.bottom + 4;
  if (left < margin) left = margin;
  if (left + W > window.innerWidth - margin) left = window.innerWidth - W - margin;
  const approxH = 124;
  if (top + approxH > window.innerHeight - margin && anchorRect.top - approxH - 4 > margin) {
    top = anchorRect.top - approxH - 4;
  }

  return createPortal(
    <div
      ref={ref}
      role="menu"
      className="fixed z-[1000] w-48 rounded-xl border border-line bg-white p-1 shadow-xl text-left font-normal"
      style={{ left, top }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button type="button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs text-ink hover:bg-slate-50" onClick={() => onSelect('full')}>
        <i className="fa-sharp fa-solid fa-file-zipper w-4 text-center text-mist" />
        Full Package (ZIP)
      </button>
      <button type="button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs text-ink hover:bg-slate-50" onClick={() => onSelect('sidc30')}>
        <i className="fa-sharp fa-solid fa-barcode w-4 text-center text-mist" />
        SIDC-30 (JSON)
      </button>
      <button type="button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs text-ink hover:bg-slate-50" onClick={() => onSelect('sidc20')}>
        <i className="fa-sharp fa-solid fa-minimize w-4 text-center text-mist" />
        SIDC-20 (JSON)
      </button>
    </div>,
    document.body
  );
}

// ───────────────────────────────────────────────────────────────────
//  Layer create/edit modal.
// ───────────────────────────────────────────────────────────────────
function LayerModal({ layer, datasets = [], onSave, onClose }) {
  const [name, setName] = useState(layer?.name || '');
  const [description, setDescription] = useState(layer?.description || '');
  const [datasetId, setDatasetId] = useState(layer?.datasetId || (datasets[0]?.id || ''));
  const [timestamp, setTimestamp] = useState(layer?.timestamp ? layer.timestamp.slice(0, 16) : new Date().toISOString().slice(0, 16));
  const [source, setSource] = useState(layer?.source || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) { setError('Layer name is required.'); return; }
    if (!datasetId) { setError('Dataset is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        datasetId,
        timestamp: timestamp ? new Date(timestamp).toISOString() : new Date().toISOString(),
        source: source.trim() || null
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/30 backdrop-blur-[2px]" onClick={onClose}>
      <form
        className="w-full max-w-md rounded-[28px] border border-line bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h3 className="mb-5 text-lg font-semibold text-ink">{layer ? 'Edit Layer' : 'Create Layer'}</h3>

        {error ? <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-soft px-4 py-2 text-sm text-red-700">{error}</div> : null}

        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Name</label>
            <input className="field w-full" value={name} onChange={(e) => setName(e.target.value)} placeholder="Layer name" required autoFocus />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Dataset</label>
            <select className="field w-full" value={datasetId} onChange={(e) => setDatasetId(e.target.value)} required>
              {datasets.sort((a, b) => a.name.localeCompare(b.name)).map((ds) => (
                <option key={ds.id} value={ds.id}>{ds.name} ({ds.type})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Timestamp</label>
            <input className="field w-full" type="datetime-local" value={timestamp} onChange={(e) => setTimestamp(e.target.value)} />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Source</label>
            <input className="field w-full" value={source} onChange={(e) => setSource(e.target.value)} placeholder="e.g. demo-map.live" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Description</label>
            <textarea className="field w-full" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description" />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving...' : layer ? 'Save' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
//  Manage Dataset Types modal — edits the editable enum that drives
//  Type select on every dataset and badge color in tables.
// ───────────────────────────────────────────────────────────────────
function DatasetTypesModal({ types, onSave, onClose }) {
  const [rows, setRows] = useState(() =>
    (types || []).map((t) => ({ ...t, _key: Math.random().toString(36).slice(2) }))
  );
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  function update(idx, patch) {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function addRow() {
    setRows((rs) => [...rs, {
      _key: Math.random().toString(36).slice(2),
      id: '', label: '', description: '', badgeClass: 'bg-slate-50 text-slate-700'
    }]);
  }
  function removeRow(idx) {
    setRows((rs) => rs.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const cleaned = rows
      .map((r) => ({
        id: r.id?.trim().toLowerCase(),
        label: r.label?.trim(),
        description: r.description?.trim() || '',
        badgeClass: r.badgeClass?.trim() || 'bg-slate-50 text-slate-700',
      }))
      .filter((r) => r.id && r.label);
    if (!cleaned.length) { setError('At least one type is required.'); return; }
    const seen = new Set();
    for (const r of cleaned) {
      if (seen.has(r.id)) { setError(`Duplicate id: "${r.id}"`); return; }
      if (!/^[a-z0-9][a-z0-9_-]{0,31}$/.test(r.id)) { setError(`Invalid id "${r.id}" — use a-z, 0-9, "-", "_" (start with a-z/0-9)`); return; }
      seen.add(r.id);
    }
    setSaving(true);
    try {
      await onSave(cleaned);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/30 backdrop-blur-[2px]" onClick={onClose}>
      <form
        className="w-full max-w-3xl rounded-[28px] border border-line bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h3 className="mb-1 text-lg font-semibold text-ink">Manage Dataset Types</h3>
        <p className="mb-4 text-xs text-mist">Edit the enum that drives the "Type" select. ID is the slug stored on each dataset; renaming an ID does not migrate existing datasets.</p>

        {error ? <div className="mb-3 rounded-2xl border border-rose-200 bg-rose-soft px-4 py-2 text-sm text-red-700">{error}</div> : null}

        <div className="max-h-[55vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white text-[11px] uppercase tracking-wider text-mist">
              <tr className="border-b border-line">
                <th className="py-2 pr-2 text-left font-semibold w-32">id</th>
                <th className="py-2 pr-2 text-left font-semibold w-32">Label</th>
                <th className="py-2 pr-2 text-left font-semibold">Description</th>
                <th className="py-2 pr-2 text-left font-semibold w-44">Badge class</th>
                <th className="py-2 pr-2 text-left font-semibold w-20">Preview</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r._key} className="border-b border-line last:border-0">
                  <td className="py-1.5 pr-2"><input className="field w-full text-xs font-mono" value={r.id} onChange={(e) => update(i, { id: e.target.value })} placeholder="my-domain" /></td>
                  <td className="py-1.5 pr-2"><input className="field w-full text-xs" value={r.label} onChange={(e) => update(i, { label: e.target.value })} placeholder="My Domain" /></td>
                  <td className="py-1.5 pr-2"><input className="field w-full text-xs" value={r.description} onChange={(e) => update(i, { description: e.target.value })} placeholder="Description" /></td>
                  <td className="py-1.5 pr-2"><input className="field w-full text-xs font-mono" value={r.badgeClass} onChange={(e) => update(i, { badgeClass: e.target.value })} placeholder="bg-slate-50 text-slate-700" /></td>
                  <td className="py-1.5 pr-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${r.badgeClass || 'bg-slate-50 text-slate-700'}`}>{r.label || r.id || 'preview'}</span></td>
                  <td className="py-1.5 text-right"><button type="button" className="text-mist hover:text-rose-600" onClick={() => removeRow(i)} title="Remove"><i className="fa-sharp fa-solid fa-xmark" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3">
          <button type="button" className="btn-secondary text-xs" onClick={addRow}>
            <i className="fa-sharp fa-solid fa-plus mr-1.5" />Add row
          </button>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
//  Dataset create/edit modal.
// ───────────────────────────────────────────────────────────────────
function DatasetModal({ dataset, datasetTypes, onSave, onClose }) {
  const [name, setName] = useState(dataset?.name || '');
  const [description, setDescription] = useState(dataset?.description || '');
  const [type, setType] = useState(dataset?.type || (datasetTypes?.[0]?.id || 'operational'));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) { setError('Dataset name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({ name: name.trim(), description: description.trim(), type });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/30 backdrop-blur-[2px]" onClick={onClose}>
      <form
        className="w-full max-w-md rounded-[28px] border border-line bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h3 className="mb-5 text-lg font-semibold text-ink">{dataset ? 'Edit Dataset' : 'Create Dataset'}</h3>

        {error ? <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-soft px-4 py-2 text-sm text-red-700">{error}</div> : null}

        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Name</label>
            <input className="field w-full" value={name} onChange={(e) => setName(e.target.value)} placeholder="Dataset name" required autoFocus />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Type</label>
            <select className="field w-full" value={type} onChange={(e) => setType(e.target.value)}>
              {(datasetTypes || []).map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Description</label>
            <textarea className="field w-full" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description" />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving...' : dataset ? 'Save' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
//  "Not installed" banner for the unreachable / not-configured case.
// ───────────────────────────────────────────────────────────────────
function NotInstalledBanner({ status }) {
  const reason = status?.reason;
  let title = 'COP ADMS is not installed on this coord';
  let detail;

  if (reason === 'not_configured') {
    detail = (
      <>
        Set <code className="font-mono text-xs">COP_BASE_URL</code> and{' '}
        <code className="font-mono text-xs">COP_API_KEY</code> in the coord{' '}
        <code className="font-mono text-xs">/opt/adms/tms/.env</code>, then
        restart <code className="font-mono text-xs">tms-adms.service</code>.
        The API key is the{' '}
        <code className="font-mono text-xs">API_KEY</code> value from{' '}
        <code className="font-mono text-xs">/opt/adms/ad-cop-service/.env</code>.
      </>
    );
  } else if (reason === 'connect_refused' || reason === 'dns_failed') {
    detail = (
      <>
        Coord is configured (<code className="font-mono text-xs">COP_BASE_URL={status?.copBaseUrl}</code>),
        but <code className="font-mono text-xs">ad-cop-service</code> is not
        running at that address. Start it on the host (or correct{' '}
        <code className="font-mono text-xs">COP_BASE_URL</code>).
      </>
    );
  } else if (reason === 'timeout' || reason === 'connection_reset') {
    detail = (
      <>
        <code className="font-mono text-xs">ad-cop-service</code> is
        reachable but slow or restarting. The page will retry on the next
        manual refresh.
      </>
    );
  } else {
    detail = (
      <>
        Upstream returned <code className="font-mono text-xs">{reason || 'unknown'}</code>.
        Check the journal of <code className="font-mono text-xs">tms-adms</code> and{' '}
        <code className="font-mono text-xs">ad-cop-service</code>.
      </>
    );
  }
  return (
    <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <div className="flex items-start gap-2">
        <i className="fa-sharp fa-solid fa-triangle-exclamation mt-0.5" aria-hidden="true" />
        <div className="min-w-0">
          <div className="font-semibold">{title}</div>
          <div className="mt-1 leading-relaxed">{detail}</div>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
//  Main page component.
// ───────────────────────────────────────────────────────────────────
export function CopDatasetsPage({ currentUser }) {
  const toast = useToast();
  const tz = currentUser?.timezone || 'UTC';
  const isAdmin = currentUser?.role === 'admin';

  // Reference-style toast helper: a single `notify(msg)` that picks
  // success vs error tone from the call-site (success path = green,
  // catch path = red).
  const notify = useCallback((msg, variant = 'info') => {
    if (!msg) return;
    if (variant === 'error') toast.error(msg);
    else if (variant === 'success') toast.success(msg);
    else toast.info(msg);
  }, [toast]);

  const formatDate = useCallback((iso) => iso ? formatUtcInTimezone(iso, tz) : '---', [tz]);

  // ── Reachability status ──────────────────────────────────────────
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);

  // ── COP state ────────────────────────────────────────────────────
  const [layers, setLayers] = useState([]);
  const [stats, setStats] = useState({ layers: 0, objects: 0 });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editLayer, setEditLayer] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [dsImporting, setDsImporting] = useState(false);
  const [showExport, setShowExport] = useState(null);
  const [cacheBusy, setCacheBusy] = useState(null);
  const [showCreateDataset, setShowCreateDataset] = useState(false);
  const [editDataset, setEditDataset] = useState(null);
  const [importTarget, setImportTarget] = useState(null);
  const importFileRef = useRef(null);
  const dsImportFileRef = useRef(null);

  const [copDatasets, setCopDatasets] = useState([]);
  const [datasetTypes, setDatasetTypes] = useState([]);
  const [showTypesModal, setShowTypesModal] = useState(false);

  // v1.10 — persona registry for Source column UUID → callsign display.
  // Mobile envelopes ship `source = "android:" + personUuid` (canonical
  // transport identifier) per the project axiom; coord resolves to
  // callsign at render time via the persons API. Eliminates callsign
  // collisions in storage while keeping the operator-friendly display.
  const [personsByUuid, setPersonsByUuid] = useState(new Map());

  useEffect(() => {
    api.persons.list().then((r) => {
      const m = new Map();
      for (const p of (r?.persons || [])) {
        if (p && p.uuid) m.set(String(p.uuid).toLowerCase(), p);
      }
      setPersonsByUuid(m);
    }).catch(() => {});
  }, []);

  /** Render-time source decoration: `android:<uuid>` and
   *  `android-intel:<uuid>` become `android:<callsign>` /
   *  `android-intel:<callsign>` when the registry resolves; falls back
   *  to the original string when (a) no recognised prefix (federation
   *  imports ship raw hostnames, etc), (b) UUID not in registry
   *  (operator removed but their data still in ad-cop-service). */
  const decorateSource = useCallback((source) => {
    if (!source) return '---';
    const s = String(source);
    // Layer-source prefix (`android-intel:`) checked first because it
    // shares the `android:` prefix's leading chars — startsWith would
    // match either if order were reversed.
    const prefixes = ['android-intel:', 'android:'];
    for (const pref of prefixes) {
      if (!s.startsWith(pref)) continue;
      const suffix = s.slice(pref.length);
      const p = personsByUuid.get(suffix.toLowerCase());
      if (p && p.callsign) return `${pref}${p.callsign}`;
      return s;
    }
    return s;
  }, [personsByUuid]);

  const typesById = useMemo(() => {
    const map = new Map();
    for (const t of datasetTypes) map.set(t.id, t);
    return map;
  }, [datasetTypes]);

  const badgeClassFor = useCallback((typeId) =>
    typesById.get(typeId)?.badgeClass || 'bg-slate-100 text-slate-600', [typesById]);
  const labelFor = useCallback((typeId) =>
    typesById.get(typeId)?.label || typeId, [typesById]);

  // ── AWDS state ────────────────────────────────────────────────────
  const [awdsLayers, setAwdsLayers] = useState([]);
  const [awdsStats, setAwdsStats] = useState({ layers: 0, items: 0 });
  const [awdsLoading, setAwdsLoading] = useState(true);
  const [awdsExpandedKey, setAwdsExpandedKey] = useState(null);
  const [awdsSyncing, setAwdsSyncing] = useState(false);

  // ── Loaders ──────────────────────────────────────────────────────
  const loadLayers = useCallback(async () => {
    setLoading(true);
    try {
      const [layersRes, statsRes, datasetsRes, typesRes] = await Promise.all([
        api.cop.getLayers(),
        api.cop.getLayerStats(),
        api.cop.getDatasets(),
        api.cop.getDatasetTypes(),
      ]);
      setLayers(layersRes.layers || []);
      setStats(statsRes || { layers: 0, objects: 0 });
      setCopDatasets(datasetsRes.datasets || []);
      setDatasetTypes(typesRes.types || []);
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  const loadAwdsLayers = useCallback(async () => {
    setAwdsLoading(true);
    try {
      const [layersRes, statusRes] = await Promise.all([
        api.cop.getAwdsLayers(),
        api.cop.getAwdsStatus(),
      ]);
      setAwdsLayers(layersRes.layers || []);
      setAwdsStats(statusRes?.store || { layers: 0, items: 0 });
    } catch {
      // AWDS may not be configured — silently ignore (per reference)
      setAwdsLayers([]);
      setAwdsStats({ layers: 0, items: 0 });
    } finally {
      setAwdsLoading(false);
    }
  }, []);

  // Probe reachability first; load data only when reachable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const st = await api.cop.status();
        if (cancelled) return;
        setStatus(st);
        if (st?.reachable) {
          loadLayers();
          loadAwdsLayers();
        } else {
          setLoading(false);
          setAwdsLoading(false);
        }
      } catch (err) {
        if (cancelled) return;
        // ApiError 503 — wrap into status shape for the banner.
        if (err instanceof ApiError && err.status === 503) {
          setStatus(err.payload || { reachable: false, reason: 'unknown' });
        } else {
          setStatusError(err.message);
        }
        setLoading(false);
        setAwdsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadLayers, loadAwdsLayers]);

  // Auto-refresh when tab regains focus (reference behaviour).
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible' && status?.reachable) loadLayers();
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadLayers, status?.reachable]);

  // ── Handlers ─────────────────────────────────────────────────────
  async function handleSaveTypes(types) {
    const res = await api.cop.updateDatasetTypes(types);
    setDatasetTypes(res.types || []);
    setShowTypesModal(false);
    notify(`Dataset types saved (${(res.types || []).length})`, 'success');
  }

  async function handleCreate(data) {
    await api.cop.createLayer(data);
    setShowCreate(false);
    notify('Layer created', 'success');
    await loadLayers();
  }

  async function handleEdit(data) {
    await api.cop.updateLayer(editLayer.id, data);
    setEditLayer(null);
    notify('Layer updated', 'success');
    await loadLayers();
  }

  async function handleDelete(layer) {
    const msg = layer.objectCount > 0
      ? `Delete layer "${layer.name}" and all ${layer.objectCount} objects inside?`
      : `Delete layer "${layer.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await api.cop.deleteLayer(layer.id);
      notify('Layer deleted', 'success');
      if (expandedId === layer.id) setExpandedId(null);
      await loadLayers();
    } catch (err) {
      notify(err.message, 'error');
    }
  }

  async function handleCloneLayer(layer) {
    const name = window.prompt('Clone layer name:', layer.name + ' (copy)');
    if (!name) return;
    try {
      await api.cop.cloneLayer(layer.id, { name });
      notify('Layer cloned (without cache)', 'success');
      await loadLayers();
    } catch (err) {
      notify(err.message, 'error');
    }
  }

  async function handleCreateDataset(data) {
    await api.cop.createDataset(data);
    setShowCreateDataset(false);
    notify('Dataset created', 'success');
    await loadLayers();
  }

  async function handleEditDataset(data) {
    await api.cop.updateDataset(editDataset.id, data);
    setEditDataset(null);
    notify('Dataset updated', 'success');
    await loadLayers();
  }

  async function handleDeleteDataset(ds) {
    if (!window.confirm(`Delete dataset "${ds.name}"?`)) return;
    try {
      await api.cop.deleteDataset(ds.id);
      notify('Dataset deleted', 'success');
      await loadLayers();
    } catch (err) {
      notify(err.message, 'error');
    }
  }

  function startImport(type) {
    if (copDatasets.length === 0) {
      notify('Create a dataset first', 'error');
      return;
    }
    setImportTarget({ type, datasetId: copDatasets[0]?.id || '' });
  }

  async function handleImport(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const datasetId = importTarget?.datasetId;
    setImportTarget(null);
    setImporting(true);
    try {
      const result = await api.cop.importLayer(file, datasetId);
      notify(`Layer imported: ${result.imported} objects`, 'success');
      await loadLayers();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setImporting(false);
      if (importFileRef.current) importFileRef.current.value = '';
    }
  }

  async function handleDeepstateImport(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const datasetId = importTarget?.datasetId;
    setImportTarget(null);
    setDsImporting(true);
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      if (!json.map?.features) throw new Error('Invalid DeepState JSON: missing map.features');
      const result = await api.cop.importDeepstate(json, datasetId);
      const s = result.stats;
      notify(`DeepState imported: ${s.objectsCreated} objects (${s.pointsInput} markers, ${s.polygonsInput} areas)`, 'success');
      await loadLayers();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setDsImporting(false);
      if (dsImportFileRef.current) dsImportFileRef.current.value = '';
    }
  }

  function handleExport(layerId, format) {
    setShowExport(null);
    const url = api.cop.exportLayerUrl(layerId, format);
    window.open(url, '_blank');
  }

  // ── Cache renderers (SVG + MilSymbol) ─────────────────────────────
  // Both buttons stay visible per operator's contract — the UI must
  // mirror the reference page even when the renderer engine is not
  // installed on this deployment. The handlers below show "Renderer
  // not installed" instead of running. When a renderer IS installed:
  //
  //   - Render SVG cache (green paintbrush, client-side) — needs the
  //     `createInstanceCacheSvg` helper from the Layers editor (~2100
  //     lines + milsymbol vendor). Replace the stub with the loop from
  //     reference layers-tab.jsx#handleRenderCache:
  //         load classifier (api.cop.getTypes + api.cop.getSymbolLayers)
  //         + layer objects + foreach object render SVG +
  //         batch-upload via api.cop.saveLayerCache.
  //
  //   - Render MilSymbol cache (blue shield, server-side) — needs the
  //     `/api/cop-layers/:id/cache/milsymbol` route + milsymbol vendor
  //     on ad-cop-service. When ad-cop-service has it, swap the stub
  //     for `await api.cop.renderLayerMilsymbol(layer.id)`.
  //
  // Until both prerequisites land, both buttons surface the same
  // operator-facing "not installed" message.
  function rendererNotInstalled(layer) {
    if (cacheBusy) return;
    notify(`Renderer not installed — cannot generate cache for "${layer.name}"`, 'error');
  }
  const handleRenderCache = rendererNotInstalled;
  const handleRenderMilsymbol = rendererNotInstalled;

  async function handleDeleteCache(layer) {
    if (cacheBusy) return;
    if (!window.confirm(`Delete SVG cache for all objects in "${layer.name}"?`)) return;
    setCacheBusy(layer.id);
    try {
      const res = await api.cop.deleteLayerCache(layer.id);
      notify(`Cache deleted: ${res.deleted} objects`, 'success');
      await loadLayers();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setCacheBusy(null);
    }
  }

  async function handleStripStyles(layer) {
    if (cacheBusy) return;
    if (!window.confirm(`Strip baked styles from polylines/polygons in "${layer.name}"? Markers stay as-is. Layer file will shrink and styles will be resolved live from the classifier.`)) return;
    setCacheBusy(layer.id);
    try {
      const res = await api.cop.stripLayerStyles(layer.id);
      notify(`Styles stripped: ${res.stripped} objects (faceless mode)`, 'success');
      await loadLayers();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setCacheBusy(null);
    }
  }

  async function handleAwdsDelete(layer) {
    const msg = `Delete AWDS layer "${layer.type} / ${layer.dataType}" (${formatDate(layer.timestamp)})?`;
    if (!window.confirm(msg)) return;
    try {
      await api.cop.deleteAwdsLayer(layer.id);
      notify('AWDS layer deleted', 'success');
      await loadAwdsLayers();
    } catch (err) {
      notify(err.message, 'error');
    }
  }

  async function handleAwdsDeleteDataset(ds) {
    const msg = `Delete dataset "${ds.type} / ${ds.dataType} / ${ds.regionName}" and all ${ds.layers.length} layer(s)?`;
    if (!window.confirm(msg)) return;
    try {
      let deleted = 0;
      for (const layer of ds.layers) {
        await api.cop.deleteAwdsLayer(layer.id);
        deleted++;
      }
      notify(`Deleted ${deleted} AWDS layers`, 'success');
      await loadAwdsLayers();
    } catch (err) {
      notify(err.message, 'error');
    }
  }

  async function handleAwdsExport(layerId) {
    // Force browser download via Blob + <a download>. Symmetric with COP
    // export (where the server sets Content-Disposition: attachment).
    // The AWDS layer route /api/awds/layers/:id returns inline JSON
    // without an attachment header (the same endpoint is reused by
    // federation and inline reads), so window.open() would render JSON
    // in a new tab instead of saving. Fetching + Blob download forces
    // a save dialog regardless of headers. Fix ported from
    // manuals/COP-microservice-integration/Datasets/layers-tab.jsx.
    const url = api.cop.exportAwdsLayerUrl(layerId);
    let objectUrl = null;
    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || `Export failed: ${response.status}`);
      }
      const blob = await response.blob();
      objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `awds-layer-${layerId}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      notify(`AWDS export failed: ${err.message}`, 'error');
    } finally {
      if (objectUrl) {
        // Defer revoke a tick so the browser can start the download.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
    }
  }

  async function handleAwdsDeleteArchive() {
    if (!window.confirm('Delete all archive layers (timestamp older than 3 hours)?')) return;
    try {
      const res = await api.cop.deleteAwdsArchiveLayers();
      notify(`Deleted ${res.deleted} archive layers`, 'success');
      await loadAwdsLayers();
    } catch (err) {
      notify(err.message, 'error');
    }
  }

  async function handleCopDeleteAll() {
    if (!window.confirm('Delete ALL COP datasets and layers? This is irreversible.')) return;
    if (!window.confirm('Really delete every dataset, layer, and object? This cannot be undone.')) return;
    try {
      const res = await api.cop.deleteAllLayers();
      notify(`Deleted ${res.deletedDatasets} datasets, ${res.deletedLayers} layers, ${res.deletedObjects} objects`, 'success');
      await loadLayers();
    } catch (err) {
      notify(err.message, 'error');
    }
  }

  async function handleAwdsDeleteAll() {
    if (!window.confirm('Delete ALL AWDS layers? They will be re-created on next sync.')) return;
    try {
      const res = await api.cop.deleteAllAwdsLayers();
      notify(`Deleted ${res.deleted} layers`, 'success');
      await loadAwdsLayers();
    } catch (err) {
      notify(err.message, 'error');
    }
  }

  async function handleAwdsSync() {
    setAwdsSyncing(true);
    try {
      await api.cop.syncAwds();
      notify('Sync complete', 'success');
      await loadAwdsLayers();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setAwdsSyncing(false);
    }
  }

  // ── Render ───────────────────────────────────────────────────────
  return (
    <PageShell
      icon="fa-layer-group"
      title="Datasets"
      subtitle="Manage COP datasets and layers from the local COP service"
      actions={
        status?.reachable ? (
          <button
            type="button"
            onClick={() => { loadLayers(); loadAwdsLayers(); }}
            disabled={loading || awdsLoading}
            className="btn-secondary text-xs"
          >
            <i className={`fa-sharp fa-solid fa-arrows-rotate ${(loading || awdsLoading) ? 'fa-spin' : ''}`} aria-hidden="true" />
            Refresh
          </button>
        ) : null
      }
    >
      {statusError && (
        <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-soft px-4 py-3 text-sm text-rose-700">
          <i className="fa-sharp fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
          {statusError}
        </div>
      )}
      {status && !status.reachable && <NotInstalledBanner status={status} />}
      {!status?.reachable ? null : (
        <div className="space-y-5">
          {/* ── Stat tiles ───────────────────────────────────────── */}
          <div>
            <h2 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-mist">
              <i className="fa-sharp fa-solid fa-chart-simple text-slate-400" /> Overview
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <StatCard label="COP Datasets / Layers" value={`${copDatasets.length} / ${stats.layers}`} tone="accent" icon="fa-sharp fa-solid fa-layer-group" helper="Datasets and layers" />
              <StatCard label="AWDS Datasets / Layers" value={`${new Set(awdsLayers.map((l) => l.type + ':' + l.dataType + ':' + (l.region?.name || ''))).size} / ${awdsLayers.length}`} tone="success" icon="fa-sharp fa-solid fa-cloud-sun" helper="Datasets and layers" />
              <StatCard label="Storage" value={layers[0]?.storageType || 'json'} tone="violet" icon="fa-sharp fa-solid fa-database" helper="Current storage backend" />
              <StatCard label="Last Updated COP" value={layers.length ? formatDate(layers.reduce((a, b) => a.updatedAt > b.updatedAt ? a : b).updatedAt) : '---'} tone="orange" icon="fa-sharp fa-solid fa-clock" helper="Most recent COP layer update" />
              <StatCard label="Last Updated AWDS" value={awdsLayers.length ? formatDate(awdsLayers.reduce((a, b) => (a.createdAt || '') > (b.createdAt || '') ? a : b).createdAt) : '---'} tone="teal" icon="fa-sharp fa-solid fa-cloud-sun" helper="Most recent AWDS layer sync" />
            </div>
          </div>

          {/* ── COP Datasets header / toolbar ───────────────────── */}
          <section className="rounded-[28px] border border-line bg-white p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="mb-2 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-slate-600">
                  COP Data
                </div>
                <h3 className="text-xl font-semibold text-ink">COP Datasets</h3>
                <p className="mt-2 text-sm leading-7 text-mist">
                  MIL-STD object datasets and layers.{' '}
                  <span className="tabular-nums">{copDatasets.length} datasets, {stats.layers} layers, {stats.objects} objects</span>
                </p>
              </div>
              {isAdmin && (
                <div className="flex flex-wrap gap-2">
                  <button className="btn-primary" onClick={() => setShowCreateDataset(true)}>
                    <i className="fa-sharp fa-solid fa-plus" />
                    Create Dataset
                  </button>
                  <button className="btn-secondary" onClick={() => setShowTypesModal(true)} title="Edit the list of dataset types (domains)">
                    <i className="fa-sharp fa-solid fa-tags" />
                    Manage Types
                  </button>
                  <button className="btn-secondary" onClick={() => setShowCreate(true)}>
                    <i className="fa-sharp fa-solid fa-plus" />
                    Create Layer
                  </button>
                  <button className="btn-secondary" onClick={() => startImport('import')} disabled={importing}>
                    <i className="fa-sharp fa-solid fa-file-import" />
                    {importing ? 'Importing...' : 'Import'}
                  </button>
                  <button className="btn-secondary !text-amber-700 hover:!bg-amber-50" onClick={() => startImport('deepstate')} disabled={dsImporting}>
                    <i className="fa-sharp fa-solid fa-map-location-dot" />
                    {dsImporting ? 'Importing...' : 'DeepState'}
                  </button>
                  <button className="btn-secondary !text-red-600 hover:!bg-red-50" onClick={handleCopDeleteAll}>
                    <i className="fa-sharp fa-solid fa-trash" />
                    Delete All
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* ── COP Datasets table (with nested layers) ────────── */}
          <section className="rounded-[28px] border border-line bg-white p-5">
            {loading ? (
              <div className="py-10 text-center text-sm text-mist">Loading datasets...</div>
            ) : copDatasets.length === 0 ? (
              <div className="py-10 text-center text-sm text-mist">No datasets created yet. Click "Create Dataset" to start.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-line text-xs font-semibold uppercase tracking-[0.12em] text-mist">
                      <th className="pb-3 pr-4" style={{ width: 40 }}></th>
                      <th className="pb-3 pr-4">Name</th>
                      <th className="pb-3 pr-4">Type</th>
                      <th className="pb-3 pr-4">Description</th>
                      <th className="pb-3 pr-4 text-right">Layers</th>
                      <th className="pb-3 pr-4 text-right">Objects</th>
                      <th className="pb-3 pr-4">Range</th>
                      {isAdmin && <th className="pb-3 text-right">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const sortedDatasets = [...copDatasets].sort((a, b) => a.name.localeCompare(b.name));
                      const orphanLayers = layers.filter((l) => !l.datasetId || !copDatasets.some((d) => d.id === l.datasetId));
                      if (orphanLayers.length > 0) {
                        sortedDatasets.push({
                          id: '__uncategorized__',
                          name: 'Uncategorized',
                          type: 'none',
                          description: 'Layers without a dataset assignment',
                          _isVirtual: true,
                        });
                      }
                      return sortedDatasets;
                    })().map((ds) => {
                      const dsLayers = (ds._isVirtual
                        ? layers.filter((l) => !l.datasetId || !copDatasets.some((d) => d.id === l.datasetId))
                        : layers.filter((l) => l.datasetId === ds.id)
                      ).sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));
                      const totalObjects = dsLayers.reduce((s, l) => s + (l.objectCount || 0), 0);
                      const oldest = dsLayers.length ? dsLayers[0].timestamp : null;
                      const newest = dsLayers.length ? dsLayers[dsLayers.length - 1].timestamp : null;
                      const isExpanded = expandedId === ds.id;
                      return (
                        <Fragment key={ds.id}>
                          <tr className="border-b border-line last:border-0 hover:bg-slate-50">
                            <td className="py-3 pr-4">
                              <button
                                className="inline-flex h-6 w-6 items-center justify-center rounded-lg text-xs text-mist hover:bg-slate-100"
                                onClick={() => setExpandedId(isExpanded ? null : ds.id)}
                                title={isExpanded ? 'Collapse' : 'Expand layers'}
                              >
                                <i className={`fa-sharp fa-solid ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                              </button>
                            </td>
                            <td className="py-3 pr-4 font-medium text-ink">
                              <div className="inline-flex items-center gap-2">
                                <span>{ds.name}</span>
                                {!ds._isVirtual && (
                                  <button
                                    className="inline-flex h-5 w-5 items-center justify-center rounded text-[10px] text-mist hover:bg-slate-100 hover:text-ink"
                                    onClick={() => { navigator.clipboard.writeText(ds.id); notify('Dataset ID copied', 'success'); }}
                                    title={`Copy ID: ${ds.id}`}
                                  >
                                    <i className="fa-sharp fa-regular fa-copy" />
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="py-3 pr-4">
                              <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${
                                ds.type === 'none' ? 'bg-rose-50 text-rose-700' : badgeClassFor(ds.type)
                              }`}>
                                {ds.type === 'none' ? 'none' : labelFor(ds.type)}
                              </span>
                            </td>
                            <td className="py-3 pr-4 text-mist">{ds.description || '---'}</td>
                            <td className="py-3 pr-4 text-right tabular-nums font-mono text-xs text-mist">{dsLayers.length}</td>
                            <td className="py-3 pr-4 text-right tabular-nums font-mono text-xs text-mist">{totalObjects}</td>
                            <td className="py-3 pr-4 text-xs text-mist tabular-nums">
                              {oldest && newest ? `${formatDate(oldest)} → ${formatDate(newest)}` : '---'}
                            </td>
                            {isAdmin && (
                              <td className="py-3 text-right">
                                {ds._isVirtual ? (
                                  <span className="text-[11px] text-mist italic">reassign layers to a dataset</span>
                                ) : (
                                  <div className="flex justify-end gap-2">
                                    <button className="btn-secondary !px-3 !py-1.5 text-xs" onClick={() => setEditDataset(ds)}>
                                      <i className="fa-sharp fa-solid fa-pen-to-square" />
                                      Edit
                                    </button>
                                    <button
                                      className="btn-secondary !px-3 !py-1.5 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                                      onClick={() => handleDeleteDataset(ds)}
                                      title={dsLayers.length > 0 ? `Cannot delete: ${dsLayers.length} layers use this dataset` : 'Delete dataset'}
                                    >
                                      <i className="fa-sharp fa-solid fa-trash" />
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </td>
                            )}
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={isAdmin ? 8 : 7} className="bg-slate-50 px-4 py-3">
                                {dsLayers.length === 0 ? (
                                  <div className="py-4 text-center text-xs text-mist">No layers in this dataset.</div>
                                ) : (
                                  <div className="overflow-x-auto rounded-xl border border-line bg-white">
                                    <table className="w-full text-left text-xs">
                                      <thead>
                                        <tr className="border-b border-line bg-slate-50 text-[10px] font-semibold uppercase tracking-[0.12em] text-mist">
                                          <th className="px-3 py-2">Name</th>
                                          <th className="px-3 py-2">Timestamp</th>
                                          <th className="px-3 py-2 text-right">Objects</th>
                                          <th className="px-3 py-2">Source</th>
                                          <th className="px-3 py-2">Created</th>
                                          {isAdmin && <th className="px-3 py-2 text-right">Actions</th>}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {dsLayers.map((layer) => (
                                          <tr key={layer.id} className="border-b border-line last:border-0 hover:bg-slate-50/60">
                                            <td className="px-3 py-2 font-medium text-ink">
                                              <div className="inline-flex items-center gap-2">
                                                <span>{layer.name}</span>
                                                <button
                                                  className="inline-flex h-5 w-5 items-center justify-center rounded text-[10px] text-mist hover:bg-slate-100 hover:text-ink"
                                                  onClick={() => { navigator.clipboard.writeText(layer.id); notify('Layer ID copied', 'success'); }}
                                                  title={`Copy ID: ${layer.id}`}
                                                >
                                                  <i className="fa-sharp fa-regular fa-copy" />
                                                </button>
                                              </div>
                                            </td>
                                            <td className="px-3 py-2 text-mist tabular-nums">{formatDate(layer.timestamp)}</td>
                                            <td className="px-3 py-2 text-right tabular-nums font-mono text-mist">{layer.objectCount}</td>
                                            <td className="px-3 py-2 text-mist">{decorateSource(layer.source)}</td>
                                            <td className="px-3 py-2 text-mist">{formatDate(layer.createdAt)}</td>
                                            {isAdmin && (
                                              <td className="px-3 py-2 text-right">
                                                <div className="flex justify-end gap-2">
                                                  <button className="btn-secondary !px-2 !py-1 text-[10px]" onClick={() => handleCloneLayer(layer)} title="Clone without cache">
                                                    <i className="fa-sharp fa-solid fa-clone" />
                                                  </button>
                                                  <button
                                                    className="btn-secondary !px-2 !py-1 text-[10px]"
                                                    onClick={(e) => {
                                                      if (showExport?.layerId === layer.id) {
                                                        setShowExport(null);
                                                      } else {
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        setShowExport({ layerId: layer.id, rect });
                                                      }
                                                    }}
                                                    title="Export"
                                                  >
                                                    <i className="fa-sharp fa-solid fa-file-export" />
                                                  </button>
                                                  {layer.objectCount > 0 && (
                                                    <button
                                                      className="btn-secondary !px-2 !py-1 text-[10px] text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                                                      onClick={() => handleStripStyles(layer)}
                                                      disabled={!!cacheBusy}
                                                      title="Strip baked styles from polylines/polygons (faceless mode)"
                                                    >
                                                      <i className="fa-sharp fa-solid fa-eraser" />
                                                    </button>
                                                  )}
                                                  {layer.cachedCount > 0 ? (
                                                    <button
                                                      className="btn-secondary !px-2 !py-1 text-[10px] text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                                                      onClick={() => handleDeleteCache(layer)}
                                                      disabled={cacheBusy === layer.id}
                                                      title="Delete cache"
                                                    >
                                                      <i className={cacheBusy === layer.id ? 'fa-sharp fa-solid fa-spinner fa-spin' : 'fa-sharp fa-solid fa-broom'} />
                                                      {cacheBusy === layer.id ? '' : ` ${layer.cachedCount}`}
                                                    </button>
                                                  ) : layer.objectCount > 0 ? (
                                                    <>
                                                      {/* Green paintbrush — client-side SVG cache render.
                                                          Per operator contract: button stays visible even
                                                          when the renderer isn't installed; click surfaces
                                                          "Renderer not installed". See rendererNotInstalled
                                                          for swap-in instructions when a renderer lands. */}
                                                      <button
                                                        className="btn-secondary !px-2 !py-1 text-[10px] text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                                                        onClick={() => handleRenderCache(layer)}
                                                        disabled={!!cacheBusy}
                                                        title="Render SVG cache"
                                                      >
                                                        <i className={cacheBusy === layer.id ? 'fa-sharp fa-solid fa-spinner fa-spin' : 'fa-sharp fa-solid fa-paintbrush'} />
                                                      </button>
                                                      {/* Blue shield — server-side MilSymbol cache render. */}
                                                      <button
                                                        className="btn-secondary !px-2 !py-1 text-[10px] text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                                                        onClick={() => handleRenderMilsymbol(layer)}
                                                        disabled={!!cacheBusy}
                                                        title="Render MilSymbol cache"
                                                      >
                                                        <i className={cacheBusy === layer.id ? 'fa-sharp fa-solid fa-spinner fa-spin' : 'fa-sharp fa-solid fa-shield-halved'} />
                                                      </button>
                                                    </>
                                                  ) : null}
                                                  <button className="btn-secondary !px-2 !py-1 text-[10px]" onClick={() => setEditLayer(layer)} title="Edit">
                                                    <i className="fa-sharp fa-solid fa-pen-to-square" />
                                                  </button>
                                                  <button
                                                    className="btn-secondary !px-2 !py-1 text-[10px] text-red-600 hover:bg-red-50 hover:text-red-700"
                                                    onClick={() => handleDelete(layer)}
                                                    title="Delete"
                                                  >
                                                    <i className="fa-sharp fa-solid fa-trash" />
                                                  </button>
                                                </div>
                                              </td>
                                            )}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ── AWDS Datasets header / toolbar ──────────────────── */}
          <section className="rounded-[28px] border border-line bg-white p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="mb-2 inline-flex rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-sky-700">
                  Weather Data
                </div>
                <h3 className="text-xl font-semibold text-ink">AWDS Datasets</h3>
                <p className="mt-2 text-sm leading-7 text-mist">
                  Atmospheric and oceanographic data from Open-Meteo.{' '}
                  <span className="tabular-nums">{awdsStats.layers} layers, {awdsStats.items} items</span>
                </p>
              </div>
              {isAdmin && (
                <div className="flex flex-wrap gap-2">
                  <button className="btn-secondary text-xs" onClick={handleAwdsSync} disabled={awdsSyncing}>
                    <i className={`fa-sharp fa-solid fa-arrows-rotate ${awdsSyncing ? 'fa-spin' : ''}`} />
                    {awdsSyncing ? 'Syncing...' : 'Sync'}
                  </button>
                  <button className="btn-secondary text-xs text-amber-600 hover:bg-amber-50 hover:text-amber-700" onClick={handleAwdsDeleteArchive}>
                    <i className="fa-sharp fa-solid fa-clock-rotate-left" />
                    Delete Archive
                  </button>
                  <button className="btn-secondary text-xs text-red-600 hover:bg-red-50 hover:text-red-700" onClick={handleAwdsDeleteAll}>
                    <i className="fa-sharp fa-solid fa-trash" />
                    Delete All
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* ── AWDS Datasets table ─────────────────────────────── */}
          <section className="rounded-[28px] border border-line bg-white p-5">
            {awdsLoading ? (
              <div className="py-10 text-center text-sm text-mist">Loading AWDS datasets...</div>
            ) : awdsLayers.length === 0 ? (
              <div className="py-10 text-center text-sm text-mist">No AWDS weather data available.</div>
            ) : (() => {
              const datasetMap = {};
              for (const l of awdsLayers) {
                const regionName = l.region?.name || 'Unknown';
                const key = l.type + ':' + l.dataType + ':' + regionName;
                if (!datasetMap[key]) datasetMap[key] = { type: l.type, dataType: l.dataType, source: l.source, regionName, layers: [] };
                datasetMap[key].layers.push(l);
              }
              const dtOrder = { current: 0, hourly: 1, daily: 2 };
              const typeOrder = { atmospheric: 0, oceanographic: 1 };
              const datasets = Object.entries(datasetMap)
                .sort(([, a], [, b]) =>
                  (typeOrder[a.type] ?? 9) - (typeOrder[b.type] ?? 9) ||
                  (dtOrder[a.dataType] ?? 9) - (dtOrder[b.dataType] ?? 9) ||
                  a.regionName.localeCompare(b.regionName)
                )
                .map(([key, ds]) => {
                  ds.layers.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
                  ds.key = key;
                  ds.totalItems = ds.layers.reduce((s, l) => s + (l.itemCount || 0), 0);
                  ds.oldest = ds.layers[0].timestamp;
                  ds.newest = ds.layers[ds.layers.length - 1].timestamp;
                  return ds;
                });

              return (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-line text-xs font-semibold uppercase tracking-[0.12em] text-mist">
                        <th className="pb-3 pr-4" style={{ width: 40 }}></th>
                        <th className="pb-3 pr-4">Type</th>
                        <th className="pb-3 pr-4">Data Type</th>
                        <th className="pb-3 pr-4">Region</th>
                        <th className="pb-3 pr-4">Source</th>
                        <th className="pb-3 pr-4 text-right">Layers</th>
                        <th className="pb-3 pr-4 text-right">Items</th>
                        <th className="pb-3 pr-4">Range</th>
                        {isAdmin && <th className="pb-3 text-right">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {datasets.map((ds) => (
                        <Fragment key={ds.key}>
                          <tr className="border-b border-line last:border-0 hover:bg-slate-50">
                            <td className="py-3 pr-4">
                              <button
                                className="inline-flex h-6 w-6 items-center justify-center rounded-lg text-xs text-mist hover:bg-slate-100"
                                onClick={() => setAwdsExpandedKey(awdsExpandedKey === ds.key ? null : ds.key)}
                              >
                                <i className={`fa-sharp fa-solid ${awdsExpandedKey === ds.key ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                              </button>
                            </td>
                            <td className="py-3 pr-4">
                              <span className="rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-sky-700">
                                {ds.type}
                              </span>
                            </td>
                            <td className="py-3 pr-4">
                              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                                {ds.dataType}
                              </span>
                            </td>
                            <td className="py-3 pr-4 text-xs font-medium text-ink">{ds.regionName}</td>
                            <td className="py-3 pr-4 text-xs text-mist">{decorateSource(ds.source)}</td>
                            <td className="py-3 pr-4 text-right tabular-nums font-mono text-xs text-mist">{ds.layers.length}</td>
                            <td className="py-3 pr-4 text-right tabular-nums font-mono text-xs text-mist">{ds.totalItems}</td>
                            <td className="py-3 pr-4 text-xs text-mist tabular-nums">
                              {formatDate(ds.oldest)} → {formatDate(ds.newest)}
                            </td>
                            {isAdmin && (
                              <td className="py-3 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    className="btn-secondary !px-3 !py-1.5 text-xs"
                                    onClick={() => handleAwdsExport(ds.layers[ds.layers.length - 1].id)}
                                    title="Export latest layer"
                                  >
                                    <i className="fa-sharp fa-solid fa-file-export" />
                                    Export
                                  </button>
                                  <button
                                    className="btn-secondary !px-3 !py-1.5 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                                    onClick={() => handleAwdsDeleteDataset(ds)}
                                    title={`Delete all ${ds.layers.length} layers in this dataset`}
                                  >
                                    <i className="fa-sharp fa-solid fa-trash" />
                                    Delete
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                          {awdsExpandedKey === ds.key && (
                            <tr>
                              <td colSpan={isAdmin ? 9 : 8} className="bg-slate-50 px-4 py-3">
                                <div className="overflow-x-auto rounded-xl border border-line bg-white">
                                  <table className="w-full text-left text-xs">
                                    <thead>
                                      <tr className="border-b border-line bg-slate-50 text-[10px] font-semibold uppercase tracking-[0.12em] text-mist">
                                        <th className="px-3 py-2">Timestamp</th>
                                        <th className="px-3 py-2 text-right">Items</th>
                                        <th className="px-3 py-2">Parser</th>
                                        <th className="px-3 py-2">Created</th>
                                        {isAdmin && <th className="px-3 py-2 text-right">Actions</th>}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {ds.layers.map((layer) => (
                                        <tr key={layer.id} className="border-b border-line last:border-0 hover:bg-slate-50/60">
                                          <td className="px-3 py-2 text-ink tabular-nums">{formatDate(layer.timestamp)}</td>
                                          <td className="px-3 py-2 text-right tabular-nums font-mono text-mist">{layer.itemCount ?? '---'}</td>
                                          <td className="px-3 py-2">
                                            {layer.parser ? (
                                              <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold tracking-wider text-violet-700">
                                                {layer.parser}
                                              </span>
                                            ) : (
                                              <span className="text-mist">---</span>
                                            )}
                                          </td>
                                          <td className="px-3 py-2 text-mist">{formatDate(layer.createdAt)}</td>
                                          {isAdmin && (
                                            <td className="px-3 py-2 text-right">
                                              <div className="flex justify-end gap-2">
                                                <button
                                                  className="btn-secondary !px-2 !py-1 text-[10px]"
                                                  onClick={() => handleAwdsExport(layer.id)}
                                                  title="Export this layer"
                                                >
                                                  <i className="fa-sharp fa-solid fa-file-export" />
                                                </button>
                                                <button
                                                  className="btn-secondary !px-2 !py-1 text-[10px] text-red-600 hover:bg-red-50 hover:text-red-700"
                                                  onClick={() => handleAwdsDelete(layer)}
                                                >
                                                  <i className="fa-sharp fa-solid fa-trash" />
                                                </button>
                                              </div>
                                            </td>
                                          )}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </section>

          {/* ── Modals ──────────────────────────────────────────── */}
          {showCreate ? <LayerModal layer={null} datasets={copDatasets} onSave={handleCreate} onClose={() => setShowCreate(false)} /> : null}
          {editLayer ? <LayerModal layer={editLayer} datasets={copDatasets} onSave={handleEdit} onClose={() => setEditLayer(null)} /> : null}
          {showCreateDataset ? <DatasetModal dataset={null} datasetTypes={datasetTypes} onSave={handleCreateDataset} onClose={() => setShowCreateDataset(false)} /> : null}
          {editDataset ? <DatasetModal dataset={editDataset} datasetTypes={datasetTypes} onSave={handleEditDataset} onClose={() => setEditDataset(null)} /> : null}
          {showTypesModal ? <DatasetTypesModal types={datasetTypes} onSave={handleSaveTypes} onClose={() => setShowTypesModal(false)} /> : null}

          {/* Import dataset picker */}
          {importTarget ? (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/30 backdrop-blur-[2px]" onClick={() => setImportTarget(null)}>
              <div className="w-full max-w-sm rounded-[28px] border border-line bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <h3 className="mb-4 text-lg font-semibold text-ink">
                  {importTarget.type === 'deepstate' ? 'Import DeepState' : 'Import Layer'}
                </h3>
                <div className="mb-4">
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Target Dataset</label>
                  <select
                    className="field w-full"
                    value={importTarget.datasetId}
                    onChange={(e) => setImportTarget({ ...importTarget, datasetId: e.target.value })}
                  >
                    {copDatasets.sort((a, b) => a.name.localeCompare(b.name)).map((ds) => (
                      <option key={ds.id} value={ds.id}>{ds.name} ({ds.type})</option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-3">
                  <button className="btn-secondary" onClick={() => setImportTarget(null)}>Cancel</button>
                  <label className="btn-primary cursor-pointer">
                    <i className="fa-sharp fa-solid fa-file-import" />
                    Select File
                    {importTarget.type === 'deepstate' ? (
                      <input ref={dsImportFileRef} type="file" accept=".json" className="hidden" onChange={handleDeepstateImport} />
                    ) : (
                      <input ref={importFileRef} type="file" accept=".zip,.json" className="hidden" onChange={handleImport} />
                    )}
                  </label>
                </div>
              </div>
            </div>
          ) : null}

          {/* Portal-rendered Export menu */}
          {showExport && (
            <ExportMenu
              anchorRect={showExport.rect}
              onClose={() => setShowExport(null)}
              onSelect={(format) => handleExport(showExport.layerId, format)}
            />
          )}
        </div>
      )}
    </PageShell>
  );
}
